import { ChangeDetectionStrategy, Component, HostBinding } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import * as moment from 'moment';
import { locale as english } from '../i18n/en';
import { locale as vn } from '../i18n/vn';

const customPresets = [
    'TODAY',
    'YESTERDAY',
    'THIS_WEEK',
    'LAST_WEEK',
    'THIS_MONTH',
    'LAST_MONTH',
] as const;

type CustomPreset = typeof customPresets[number];

@Component({
    selector: 'app-artstuff-range-panel',
    templateUrl: './artstuff-range-panel.component.html',
    styleUrls: ['./artstuff-range-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ArtstuffRangePanelComponent<D> {
    readonly customPresets = customPresets;
    @HostBinding('class.touch-ui')
    readonly isTouchUi = this.picker.touchUi;

    constructor(
        private dateAdapter: DateAdapter<D>,
        private picker: MatDateRangePicker<D>,
        private _fuseTranslationLoaderSrv: FuseTranslationLoaderService,
    ) {  this._fuseTranslationLoaderSrv.loadTranslations(english, vn);}

    selectRange(rangeName: CustomPreset): void {
        const [start, end] = this.calculateDateRange(rangeName);
        this.picker.select(start);
        this.picker.select(end);
        this.picker.close();
    }

    private calculateDateRange(rangeName: CustomPreset): [start: D, end: D] {
        const today = this.today;
        const year = this.dateAdapter.getYear(today);

        switch (rangeName) {
            case 'TODAY':
                return [today, today];
            case 'YESTERDAY': {
                const yesterday = this.dateAdapter.addCalendarDays(today, -1);
                return [yesterday, yesterday];
            }
            case 'THIS_WEEK': {
                return this.calculateWeek(today);
            }
            case 'THIS_MONTH': {
                return this.calculateMonth(today);
            }
            case 'LAST_WEEK': {
                const thisDayLastWeek = this.dateAdapter.addCalendarDays(today, -7);
                return this.calculateWeek(thisDayLastWeek);
            }
            case 'LAST_MONTH': {
                const thisDayLastMonth = this.dateAdapter.addCalendarMonths(today, -1);
                return this.calculateMonth(thisDayLastMonth);
            }
            default:
                return rangeName;
        }
    }

    private calculateMonth(forDay: D): [start: D, end: D] {
        const year = this.dateAdapter.getYear(forDay);
        const month = this.dateAdapter.getMonth(forDay);
        const start = this.dateAdapter.createDate(year, month, 1);
        const end = this.dateAdapter.addCalendarDays(
            start,
            this.dateAdapter.getNumDaysInMonth(forDay) - 1
        );
        return [start, end];
    }

    private calculateWeek(forDay: D): [start: D, end: D] {
        const deltaStart =
            this.dateAdapter.getFirstDayOfWeek() -
            this.dateAdapter.getDayOfWeek(forDay);
        const start = this.dateAdapter.addCalendarDays(forDay, deltaStart);
        const end = this.dateAdapter.addCalendarDays(start, 6);
        return [start, end];
    }

    private get today(): D {
        const newDate = moment();
        const today = this.dateAdapter.getValidDateOrNull(newDate);
        if (today === null) {
            throw new Error('date failed');
        }
        return today;
    }
}
